import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ATMO, IMG } from "@/config/site";

export const SERVICES = [
  {
    n: "01",
    title: "Gartenplanung",
    to: "/gartenplanung",
    img: ATMO.editorial,
    text: "Von der ersten Idee bis zum konkreten Konzept: Flächen, Materialien, Bepflanzung und Ablauf – passend zu Grundstück und Nutzung.",
  },
  {
    n: "02",
    title: "Landschaftsbau",
    to: "/gartenbau",
    img: ATMO.terrassiert,
    text: "Wir bauen Außenräume mit hochwertigen Materialien: Modellierung, Aufbau, Randabschlüsse und saubere Übergänge.",
  },
  {
    n: "03",
    title: "Terrassen",
    to: "/terrassen-pflasterarbeiten",
    img: ATMO.stein,
    text: "Sitzplätze aus Naturstein, Platten oder Holz – mit korrektem Gefälle, sauberen Fugen und Blick für Proportion.",
  },
  {
    n: "04",
    title: "Pflasterung",
    to: "/terrassen-pflasterarbeiten",
    img: IMG.terrasse,
    text: "Wege und Einfahrten mit belastbarem Aufbau, stabilen Einfassungen und durchdachter Entwässerung.",
  },
  {
    n: "05",
    title: "Bepflanzung",
    to: "/gartenbau",
    img: ATMO.gruen,
    text: "Gräser, Stauden, Hecken und Bäume – standortgerecht ausgewählt und über Jahre gedacht.",
  },
  {
    n: "06",
    title: "Gartenpflege",
    to: "/gartenpflege",
    img: IMG.formal,
    text: "Rasen, Schnitt, Beete und Saisonarbeiten. Einmalig oder dauerhaft, damit das Ergebnis bleibt.",
  },
];

export const ServicesInteractive = () => {
  const [active, setActive] = useState(0);
  const current = SERVICES[active];

  return (
    <div data-testid="services-interactive" className="grid gap-12 lg:grid-cols-[1fr_0.85fr] lg:gap-20">
      <ul className="border-t border-[#17251C]/14">
        {SERVICES.map((s, i) => (
          <li key={s.n} className="border-b border-[#17251C]/14">
            <Link
              to={s.to}
              data-testid={`service-row-${s.n}`}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
              className="group relative flex items-baseline gap-5 py-6 md:gap-8 md:py-8"
            >
              <motion.span
                animate={{ opacity: active === i ? 1 : 0 }}
                className="absolute inset-y-0 -left-4 w-[2px] bg-[#B4623C] md:-left-6"
              />
              <span
                className={`display-xl w-10 shrink-0 text-xs tracking-[0.2em] transition-colors duration-500 ${
                  active === i ? "text-[#B4623C]" : "text-[#93A387]"
                }`}
              >
                {s.n}
              </span>
              <span className="flex-1">
                <span
                  className={`display-xl block text-3xl transition-all duration-500 md:text-[3.2rem] ${
                    active === i ? "translate-x-1 text-[#17251C]" : "text-[#2F3D2E]/65"
                  }`}
                >
                  {s.title}
                </span>
                <AnimatePresence initial={false}>
                  {active === i && (
                    <motion.span
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      className="block overflow-hidden"
                    >
                      <span className="mt-3 block max-w-md text-sm leading-relaxed text-[#6E7A69] md:text-base">
                        {s.text}
                      </span>
                      {/* Bild inline für Mobile */}
                      <span className="mt-5 block overflow-hidden rounded-[1.5rem] lg:hidden">
                        <img src={s.img} alt={`${s.title} – Hanse Garten Bremen`} className="aspect-[16/10] w-full object-cover" />
                      </span>
                    </motion.span>
                  )}
                </AnimatePresence>
              </span>
              <ArrowUpRight
                size={20}
                className={`shrink-0 transition-all duration-500 ${
                  active === i ? "text-[#B4623C] opacity-100" : "text-[#93A387] opacity-0"
                }`}
              />
            </Link>
          </li>
        ))}
      </ul>

      <div className="relative hidden lg:block">
        <div className="sticky top-32 overflow-hidden arch-mask">
          <AnimatePresence mode="wait">
            <motion.img
              key={current.img}
              src={current.img}
              alt={`${current.title} – Beispielbild Hanse Garten Bremen`}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[3/4] w-full object-cover"
            />
          </AnimatePresence>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#17251C]/85 to-transparent p-8 pt-24">
            <p className="eyebrow !text-[#DCCDB4]">Leistung {current.n}</p>
            <p className="display-xl mt-2 text-2xl text-[#F3EDE2]">{current.title}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
